<template>
  <div class="mycoupon-item"
       :class="grayPlacement ? 'gray-placement' : ''">
    <div class="content">
      <div class="info">
        <div class="coupon-unit">{{ itemInfo.discount_amount ? $t('AccountInfo.MyCouponItem.currencyUnit') :
          itemInfo.discount_rate ? $t('AccountInfo.MyCouponItem.discountUnit') : '?' }}</div>
        <div class="coupon-number"
             :class="itemInfo.discount_amount && Math.floor(Number(itemInfo.discount_amount)).toString().length > 3 ? 'coupon-number-smaller' : ''">
          {{ props.itemInfo.discount_amount ? Math.floor(Number(props.itemInfo.discount_amount))
            :
            props.itemInfo.discount_rate
              ? discountRate : '?' }}</div>
        <div class="rule">
          <div class="rule-range">{{ itemInfo.threshold_amount && itemInfo.threshold_amount > 0 ?
            `${$t('AccountInfo.MyCouponItem.singleOrderThresholdPrefix')} ${Math.floor(itemInfo.threshold_amount)}
            ${$t('AccountInfo.MyCouponItem.singleOrderThresholdSuffix')}` :
            itemInfo.activity_type !== 'all' ? $t('AccountInfo.MyCouponItem.specifiedProducts') :
              $t('AccountInfo.MyCouponItem.allProducts') }}</div>
          <div class="rule-title"
               @click="ruleClick">{{ $t('AccountInfo.MyCouponItem.rules') }} ></div>
        </div>
      </div>
      <div class="time-range">{{ dayjs(itemInfo.start_time).format("YYYY.MM.DD") }}-{{
        dayjs(itemInfo.end_time).format("YYYY.MM.DD") }}</div>
      <div class="round"></div>
    </div>
    <div class="operation"
         @click="immediateUse">
      <div class="operation-text">{{ $t('AccountInfo.MyCouponItem.useNow') }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { message } from "ant-design-vue"
import dayjs from "dayjs"
import { computed, ref, watch } from "vue"
import { useI18n } from 'vue-i18n'
import { useRouter } from "vue-router"
import type { SingleCoupon } from "../api/interface/couponInterface"

const props = defineProps<{ itemInfo: SingleCoupon, activeKey: string }>()
const { t } = useI18n()
function removeLastZero(number: number) {
  let numberStr = number.toString()
  // 如果字符串以0结尾，移除最后一个字符
  if (numberStr.endsWith('0')) {
    numberStr = numberStr.slice(0, -1)
  }
  const result = parseInt(numberStr, 10)
  return result
}
const discountRate = computed(() => {
  let result = '?'
  const rate = props.itemInfo.discount_rate
  if (rate) {
    const countRate = removeLastZero(Math.floor(Number(rate) * 100))
    if (countRate > 0) result = String(countRate)
  }
  return result
})

const emits = defineEmits(['openRuleModal', 'getMycouponList'])
function ruleClick() {
  if (grayPlacement.value) return
  const info = {
    is_stackable: props.itemInfo.is_stackable,
    activity_scope: props.itemInfo.activity_scope,
    threshold_amount: props.itemInfo.threshold_amount
  }
  emits('openRuleModal', info)
}

const grayPlacement = ref(false)
watch(() => props.activeKey, (value: string) => {
  if (value === '1') {
    grayPlacement.value = false
  } else {
    grayPlacement.value = true
  }
}, { immediate: true, deep: true })


const router = useRouter()
function immediateUse() {
  if (grayPlacement.value) return
  const endTime = new Date(props.itemInfo.end_time).getTime()
  const time = new Date().getTime()
  if (endTime >= time) {
    router.push({
      path: `/console/accounts/profile/couponProducts/${props.itemInfo.ActivityUser?.id}`
    })
  } else {
    emits('getMycouponList')
    message.warning(t('AccountInfo.MyCouponItem.expired'))
  }
}

</script>


<style lang="less" scoped>
.mycoupon-item {
  width: calc((100% - 80px) / 4);
  height: 148px;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.08);
  background: #111111;
  margin: 0 20px 20px 0;
  display: flex;

  .content {
    width: calc(100% - 53px);
    background: linear-gradient(145deg, #FFF9F7, #FFFFFF);
    position: relative;
    padding: 36px 40px 20px 40px;
    justify-content: space-between;
    display: flex;
    flex-direction: column;
  }

  .info {
    display: flex;
    width: 100%;

    .coupon-unit {
      position: absolute;
      left: 43px;
      top: 40px;
    }

    .coupon-number {
      margin-top: -24px;
      padding-left: 30px;
      text-align: center;
      font-size: 68px;
      font-family: 'Din Bold', sans-serif;
      color: #111111;
      background: linear-gradient(to bottom right, #FF9000 0%, #DE4A09 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .coupon-number-smaller {
      font-size: 48px;
      margin-top: -16px;
    }

    .rule {
      flex: 1;
      padding: 17px 0 0 5px;
      font-size: 14px;
      color: #999999;
      text-align: right;

      .rule-range {
        line-height: 16px;
        font-size: 16px;
        color: #111111;
        font-weight: bold;
      }

      .rule-title {
        padding-top: 10px;
        cursor: pointer;
      }
    }
  }

  .time-range {
    line-height: 14px;
    font-size: 14px;
    color: #DE4A09;
  }

  .operation {
    cursor: pointer;
    width: 53px;
    font-size: 18px;
    color: #FFFFFF;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    .operation-text {
      width: 18px;
    }
  }

  .round {
    background-color: #111111;
    right: -19px;
    top: calc(50% - 14px);
    position: absolute;
    width: 28px;
    height: 28px;
    border-radius: 50%;
  }
}

.gray-placement {
  background-color: #cccccc;
  color: #cccccc;

  .content {
    background: linear-gradient(0deg, #FFFFFF, #FFFFFF) !important
  }

  .coupon-number {
    background: none !important;
    -webkit-background-clip: #cccccc !important;
    -webkit-text-fill-color: #cccccc !important;
  }

  .rule-range,
  .time-range {
    color: #cccccc !important;
  }

  .rule-title {
    cursor: none;
    color: #999999;
  }

  .round {
    background-color: #cccccc !important;
  }

  .operation,
  .rule-title {
    cursor: auto !important;
  }
}
</style>
